/**
 * Descriptor de Rastreador GPS. O `unique_id` é o identificador do hardware (imutável após criar);
 * `modelo`, `senha_config`, `chip_id` e `ativo` são editáveis.
 *
 * A ficha é em abas porque as duas perguntas do operador são distintas e não se atrapalham: «onde
 * está?» (instalação, mapa, paradas) e «como configuro?» (chip, senha, os SMS que ensinam o
 * aparelho a falar). Empilhar as duas fazia o mapa empurrar a configuração para fora da tela.
 */
import { Radio } from 'lucide-react';
import { SinalRastreio } from '@/components/crud/SinalRastreio';
import { PainelAchadosRastreador } from '@/components/crud/paineis/PainelAchadosRastreador';
import { PainelDiagnostico } from '@/components/crud/paineis/PainelDiagnostico';
import { PainelHistoricoRastreador } from '@/components/crud/paineis/PainelHistoricoRastreador';
import { PainelInstalacaoGuiada } from '@/components/crud/paineis/PainelInstalacaoGuiada';
import { PainelRastreadorInstalacao } from '@/components/crud/paineis/PainelRastreadorInstalacao';
import type { RastreadorCad } from '@/lib/api';
import { definirRecurso } from '@/lib/recursos/tipos';

export const rastreador = definirRecurso<RastreadorCad>({
  chave: 'rastreadores',
  endpoint: '/cadastro/rastreadores',
  titulo: 'Rastreadores',
  tituloSingular: 'rastreador',
  descricao: 'Hardware GPS, vínculo temporal com o veículo e as paradas detectadas.',
  icone: Radio,
  capacidades: { podeEditar: true, podeExcluir: true },
  buscaPlaceholder: 'Buscar por identificador ou modelo',
  rotuloItem: (r) => r.unique_id,

  colunas: [
    { chave: 'unique_id', rotulo: 'Identificador', mono: true },
    {
      chave: 'situacao',
      rotulo: 'Sinal',
      render: (r) => <SinalRastreio situacao={r.situacao} silencioSeg={r.silencio_seg} compacto />,
    },
    {
      chave: 'veiculo_placa',
      rotulo: 'Veículo',
      // Sem veículo o aparelho não atribui posição a ninguém: a lista precisa mostrar isso, não
      // deixar a célula vazia como se fosse um dado que faltou preencher.
      render: (r) =>
        r.veiculo_placa ? (
          <span className="tipo-dado text-text">{r.veiculo_placa}</span>
        ) : (
          <span className="text-text-tertiary">sem veículo</span>
        ),
    },
    { chave: 'modelo', rotulo: 'Modelo', ocultarEm: 'md' },
  ],

  camposCriar: [
    { chave: 'unique_id', rotulo: 'Identificador (unique_id)', tipo: 'texto', obrigatorio: true },
    { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto' },
  ],

  gruposEdicao: [
    {
      id: 'dispositivo',
      titulo: 'Dispositivo',
      descricao:
        'O identificador é o do rótulo do aparelho e é por ele que o Traccar o reconhece. A senha ' +
        'é a dos comandos SMS — de fábrica, 0000.',
      campos: [
        { chave: 'unique_id', rotulo: 'Identificador (unique_id)', tipo: 'leitura' },
        { chave: 'modelo', rotulo: 'Modelo', tipo: 'texto' },
        {
          chave: 'senha_config',
          rotulo: 'Senha de configuração',
          tipo: 'texto',
          ajuda: 'Vai colada ao número do comando: 8040000, não 804 0000.',
        },
        {
          chave: 'chip_id',
          rotulo: 'Chip (SIM)',
          tipo: 'referencia',
          refEndpoint: '/cadastro/chips',
          refRotulo: 'numero',
          ajuda: 'O número que recebe os SMS de configuração — e de onde vem o APN.',
        },
        { chave: 'traccar_device_id', rotulo: 'Traccar device id', tipo: 'leitura' },
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'criado_em', rotulo: 'Criado em', tipo: 'leitura' },
      ],
    },
  ],

  abas: [
    {
      id: 'achados',
      rotulo: 'O que revelou',
      paineis: [
        {
          tipo: 'custom',
          id: 'rastreador-achados',
          titulo: 'O que o trajeto revelou',
          render: ({ item }) => <PainelAchadosRastreador rastreadorId={item.id} />,
        },
      ],
    },
    {
      id: 'localizacao',
      rotulo: 'Onde está',
      paineis: [
        {
          tipo: 'custom',
          id: 'rastreador-instalacao',
          titulo: 'Onde está',
          render: ({ item }) => <PainelRastreadorInstalacao rastreadorId={item.id} />,
        },
      ],
    },
    {
      id: 'instalacao',
      rotulo: 'Pôr para funcionar',
      paineis: [
        {
          tipo: 'custom',
          id: 'rastreador-instalacao-guiada',
          titulo: 'Pôr para funcionar',
          render: ({ item, recarregar }) => (
            <PainelInstalacaoGuiada
              rastreadorId={item.id}
              chipId={(item.chip_id as string | null) ?? null}
              traccarDeviceId={(item.traccar_device_id as number | null) ?? null}
              recarregar={recarregar}
            />
          ),
        },
        // Logo abaixo do passo a passo: quando o portão não fecha, a pergunta seguinte é «por quê?»
        {
          tipo: 'custom',
          id: 'rastreador-diagnostico',
          titulo: 'Diagnóstico',
          render: ({ item }) => <PainelDiagnostico rastreadorId={item.id} />,
        },
      ],
    },
    {
      id: 'historico',
      rotulo: 'Histórico',
      paineis: [
        {
          tipo: 'custom',
          id: 'rastreador-historico',
          titulo: 'Por onde este aparelho já passou',
          render: ({ item, recarregar }) => (
            <PainelHistoricoRastreador rastreadorId={item.id} recarregar={recarregar} />
          ),
        },
      ],
    },
    // Os campos crus do aparelho ficam numa aba à parte: quem está instalando não precisa deles, e
    // quem precisa (trocar a senha de fábrica, conferir o id do Traccar) sabe onde procurar.
    { id: 'dispositivo', rotulo: 'Dispositivo', grupos: ['dispositivo'] },
  ],
});
